/* eslint-disable react/jsx-props-no-spreading */
import { FC, useEffect } from 'react'
import ReactGA from 'react-ga4'
import type { ITemplate } from '../interfaces/templates'
import Default from './Default'

/**
 * Order Confirmation screen.
 *
 * There is no route for it in code: cinnamon builds its routes from the SCS
 * config's own `routes` array, and `defaultConfig` ships none for this — so
 * `CinnamonPaths.ORDER_CONFIRMATION` is the path an author is expected to
 * register, not one that exists. **Nothing navigates here yet either**, which
 * means the screen is unreachable until a storefront authors both the
 * container and a `pushView` onto it.
 *
 * Thin like `OrderDetail` and `OrderHistory` beside it: fire analytics,
 * delegate rendering to `Default`. `OrderConfirmationPanel`, its state copy
 * and its custom slots are authored in SCS config and resolve through
 * `importRoute`, so no layout belongs here — and the props the config cannot
 * express are injected in `useCinnamonProps`, not passed down.
 *
 * **It deliberately fetches nothing.** The other two screens call a hook on
 * mount because they are reads. This one is the tail of a *write* that
 * `useOrderConfirmation` already owns, started from the cart CTA through
 * `useCreateOrder`: the mutation, the confirmation, the poll and the stephook
 * all run there, and a settled order is in `ordersSlice.created` before this
 * screen mounts.
 *
 * Note this screen already *has* an instance of that hook — every container
 * does, via `Default` → `EventManager` → `useCreateOrderHook` — and it is
 * inert because nothing here calls `createAndConfirmOrder`. What calling the
 * hook in this file would add is a second **chain**: another instance with its
 * own poll timer and request token in `useRef`, writing the same slice on its
 * own schedule. That is COM-741's defect class, and the reason COM-779 put the
 * detail refresh behind a nonce rather than a second hook. The nonce work for
 * this screen should drive the instance `EventManager` already mounts.
 *
 * Two consequences worth naming, both recorded in `.claude/CLAUDE.md`:
 *
 * - **Reaching this route no longer kills the attempt (COM-818).**
 *   `usePushView` is `navigate()` and `<Routes>` mounts one container at a
 *   time, so arriving here unmounts the cart's `EventManager` — the instance
 *   that owns the in-flight attempt. It used to have a cleanup effect, and
 *   both halves of it were harmful: bumping the request token discarded a
 *   `createOrder` that was merely slower than the unmount, and stopping the
 *   timer stranded a poll chain that had already armed. That was the flow
 *   rather than an edge of it, and it left `creating` or `confirming` on screen
 *   for good. There is no cleanup now: Redux lives above `<Routes>`, so a late
 *   outcome still lands here, and an orphaned chain keeps ticking until it
 *   settles or spends its own `timeout` budget and reports `unknown`. Note
 *   which unmount that was: until COM-858 the route table was gated on the
 *   global `loading` flag, so the `CREATE_ORDER` spinner de-registered every
 *   route and deleted the cart container before any navigation happened — on
 *   `main` nothing navigated here at all. COM-858 narrows the gate, so arriving
 *   here by `pushAfter` is the live mechanism from now on. Earlier revisions of
 *   this comment claimed the synchronous path was unaffected and that `unknown`
 *   was unreachable; both were wrong, and a third claimed navigation caused the
 *   unmount.
 * - **A direct link shows a spinner over no order.** `creationStatus` stays
 *   `idle`, which maps to the panel's in-flight `creating`, and that is a dead
 *   end today. Not for want of copy — `defaultTexts` ships
 *   `orderConfirmation.returnLabel` in all seven locales as of #730 — but
 *   because the panel gates its exit on the `returnToConversationLabel`
 *   **prop** and nothing forwards a value to it:
 *   `ORDER_CONFIRMATION_RETURN_LABEL` exists in `defaultConstants.ts` and is
 *   referenced by no `defaultProps`, so the token resolves nowhere. A route
 *   guard is the fix and it belongs here; see `PANEL_STATUS_BY_SLICE_STATUS`
 *   in `utils/orderConfirmationPanel.ts`.
 */
const OrderConfirmation: FC<ITemplate> = ({ ...props }) => {
  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: `/${props.container.uid}` })
    window.fbq('track', 'PageView', { page: `/${props.container.uid}` })
  }, [])

  return <Default {...props} />
}

export default OrderConfirmation
